import React, { useState } from 'react';
import { Search, Filter, X, Calendar } from 'lucide-react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const PaymentFilters = ({ filters, onFilterChange, statusOptions }) => {
  const { isDarkMode } = useAdminTheme();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleInputChange = (field, value) => {
    onFilterChange({ [field]: value });
  };

  const clearFilters = () => {
    onFilterChange({
      status: 'all',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const hasActiveFilters = filters.status !== 'all' || 
                          filters.search || 
                          filters.startDate || 
                          filters.endDate;

  return (
    <div className={isDarkMode ? 'card-dark' : 'card'}>
      <div className="card-body">
        <div className="space-y-4">
          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} />
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or description..."
                value={filters.search}
                onChange={(e) => handleInputChange('search', e.target.value)}
                className={`pl-10 ${isDarkMode ? 'input-dark' : 'input'}`}
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className={isDarkMode ? 'input-dark' : 'input'}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Advanced Filters Toggle */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className={`btn flex items-center ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Advanced
              </button>
              
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className={`btn flex items-center ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvanced && (
            <div className={`border-t pt-4 transition-colors duration-300 ${
              isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date Range */}
                <div>
                  <label className={isDarkMode ? 'label-dark' : 'label'}>
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className={isDarkMode ? 'input-dark' : 'input'}
                  />
                </div>

                <div>
                  <label className={isDarkMode ? 'label-dark' : 'label'}>
                    <Calendar className="w-4 h-4 inline mr-1" />
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    className={isDarkMode ? 'input-dark' : 'input'}
                  />
                </div>
              </div>

              {/* Quick Date Filters */}
              <div className="mt-4">
                <label className={isDarkMode ? 'label-dark' : 'label'}>Quick Filters</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Today', value: 'today' },
                    { label: 'Yesterday', value: 'yesterday' },
                    { label: 'Last 7 days', value: 'last7days' },
                    { label: 'Last 30 days', value: 'last30days' },
                    { label: 'This month', value: 'thismonth' },
                    { label: 'This year', value: 'thisyear' }
                  ].map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => {
                        const now = new Date();
                        let startDate = '';
                        let endDate = '';

                        switch (filter.value) {
                          case 'today':
                            startDate = endDate = now.toISOString().split('T')[0];
                            break;
                          case 'yesterday':
                            const yesterday = new Date(now);
                            yesterday.setDate(yesterday.getDate() - 1);
                            startDate = endDate = yesterday.toISOString().split('T')[0];
                            break;
                          case 'last7days':
                            const weekAgo = new Date(now);
                            weekAgo.setDate(weekAgo.getDate() - 7);
                            startDate = weekAgo.toISOString().split('T')[0];
                            endDate = now.toISOString().split('T')[0];
                            break;
                          case 'last30days':
                            const monthAgo = new Date(now);
                            monthAgo.setDate(monthAgo.getDate() - 30);
                            startDate = monthAgo.toISOString().split('T')[0];
                            endDate = now.toISOString().split('T')[0];
                            break;
                          case 'thismonth':
                            startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
                            endDate = now.toISOString().split('T')[0];
                            break;
                          case 'thisyear':
                            startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
                            endDate = now.toISOString().split('T')[0];
                            break;
                          default:
                            break;
                        }

                        onFilterChange({ startDate, endDate });
                      }}
                      className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                        isDarkMode 
                          ? 'bg-skyone-inset-dark text-zinc-200 hover:bg-skyone-border-dark' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className={`border-t pt-4 transition-colors duration-300 ${
              isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
            }`}>
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {filters.status !== 'all' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Status: {statusOptions.find(opt => opt.value === filters.status)?.label}
                      <button
                        onClick={() => handleInputChange('status', 'all')}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {filters.search && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Search: "{filters.search}"
                      <button
                        onClick={() => handleInputChange('search', '')}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {filters.startDate && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      From: {filters.startDate}
                      <button
                        onClick={() => handleInputChange('startDate', '')}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {filters.endDate && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      To: {filters.endDate}
                      <button
                        onClick={() => handleInputChange('endDate', '')}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-400 hover:bg-primary-200 hover:text-primary-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentFilters;
